import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { Form } from 'react-final-form'; // Using standard Form for state management
import { useTranslation } from 'react-i18next';

import getPossessiveForm from '../../helpers/possessive';
import { FormLabel, FormInput, FormCodeInput, FormFeedback } from '../form';
import { PrimaryButton, BorderlessButton } from '../buttons';
import Section from '../edu/Section';
import { ArrowLeft } from '../icons';
import { DIARY_PIN_SIZE } from './index';
import { email, required, composeValidators } from '../../helpers/formValidators';

import './DiarySetup.scss';

const INITIAL_FORM_VALUES = { pin: '', email: '' };

const pinRequired = (value) =>
  (value || '').length < DIARY_PIN_SIZE ? 'This field is required.' : undefined;

const DiarySetup = ({
  firstName,
  onSubmit,
  isLoading = false,
  isSetupComplete = false,
  showIncorrectMessage = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="diary-setup">
      <div className="page-header">
        <h1>{t('diary.ownersDiary', { owner: getPossessiveForm(firstName || '') })}</h1>
      </div>

      <Section colorScheme="red">
        <div className="container container--content">
          {isSetupComplete ? (
            <div className="card diary-setup-card">
              <h3>{t('diarySetup.title')}</h3>
              <h5>{t('diarySetup.complete')}</h5>
              <NavLink className="text-link" to="/diary">
                <BorderlessButton label={t('diarySetup.backToDiary')} colorScheme="blue" IconLeft={ArrowLeft} />
              </NavLink>
            </div>
          ) : (
            <div className="card diary-setup-card">
              <h3>{t('diarySetup.title')}</h3>
              <p>{t('diarySetup.pinDescription')}</p>
              
              <Form
                onSubmit={onSubmit}
                initialValues={INITIAL_FORM_VALUES}
                render={({ handleSubmit, values, invalid, submitting }) => {
                  const pinVal = values.pin || '';
                  
                  // LIVE LOGIC: Button unlocks only when valid
                  const isButtonLocked = 
                    isLoading || 
                    submitting || 
                    invalid ||
                    pinVal.length < DIARY_PIN_SIZE;

                  return (
                    <form className="diary-setup__form" onSubmit={handleSubmit} noValidate>
                      <FormLabel center>{t('diary.pin')}</FormLabel>
                      <div className="diary-setup__pin-container">
                        <FormCodeInput
                          name="pin"
                          validate={pinRequired}
                          fields={DIARY_PIN_SIZE}
                          type="tel" 
                        />
                      </div>

                      <FormLabel center className="diary-setup__email-label">{t('diary.email')}</FormLabel>
                      <FormInput
                        name="email"
                        validate={composeValidators(required, email)}
                        required
                        placeholder={t('diary.email')}
                        type="email"
                        id="diary.email"
                        className="diary-setup__email-input"
                      />
                      
                      <p className="text-note">{t('diarySetup.emailDescription')}</p>
                      
                      {showIncorrectMessage && (
                        <FormFeedback positive={false}>
                          {t('diarySetup.incorrect')}
                        </FormFeedback>
                      )}

                      <div className="diary-setup__actions">
                        <PrimaryButton
                          label={t('diarySetup.start')}
                          type="submit"
                          disabled={isButtonLocked}
                          loading={isLoading || submitting}
                        />
                      </div>
                    </form>
                  );
                }}
              />
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

DiarySetup.propTypes = {
  firstName: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isSetupComplete: PropTypes.bool,
  showIncorrectMessage: PropTypes.bool,
};

export default DiarySetup;